import { useInsertionEffect, useRef } from "react";
import { useDrawerStack } from "./DrawerStackContext";

interface DrawerStackHostProps {
  /**
   * Close handler for each drawer's `onClose`, keyed by entry id. Entries without one close
   * with `closeFrom(id)`.
   */
  closeHandlers?: ReadonlyMap<string, () => void>;
  /** Stack indices whose routed drawers are waiting for their ancestors. */
  deferredIndices?: ReadonlySet<number>;
}

/** Base z-index for stacked drawers; each subsequent drawer is offset by 10 */
const BASE_Z_INDEX = 1300;
const Z_INDEX_STEP = 10;

/**
 * Renders the stacked drawers managed by the DrawerStackProvider.
 * Each drawer gets an incrementing z-index so it appears above the previous one.
 * Each stacked drawer has its own invisible backdrop that, when clicked,
 * closes the entire stack via onCloseAll.
 */
export function DrawerStackHost({
  closeHandlers,
  deferredIndices,
}: Readonly<DrawerStackHostProps> = {}) {
  const { stack, closeFrom, closeAll } = useDrawerStack();
  let topIndex = stack.length - 1;
  while (topIndex >= 0 && deferredIndices?.has(topIndex)) topIndex -= 1;
  const topId = stack[topIndex]?.id;
  const committedTop = useRef({ id: topId, index: topIndex });
  useInsertionEffect(() => {
    committedTop.current = { id: topId, index: topIndex };
  }, [topId, topIndex]);

  if (stack.length === 0) return null;

  return (
    <>
      {/* Render each drawer in the stack */}
      {stack.map((entry, index) => {
        if (deferredIndices?.has(index)) return null;
        const zIndex = BASE_Z_INDEX + index * Z_INDEX_STEP;
        const Component = entry.component;
        const isTopmost = index === topIndex;
        // closeFrom, unlike popDrawer, ignores a late call after the drawer has left the stack.
        const close =
          closeHandlers?.get(entry.id) ?? (() => closeFrom(entry.id));

        return (
          <Component
            key={entry.id}
            {...entry.props}
            open={true}
            onClose={
              isTopmost
                ? () => {
                    if (
                      committedTop.current.id === entry.id &&
                      committedTop.current.index === index
                    ) {
                      close();
                    }
                  }
                : () => {}
            }
            onCloseAll={closeAll}
            zIndex={zIndex}
            depth={index}
          />
        );
      })}
    </>
  );
}
